/* ===== Required css ===== */
import './styles/App.css';
import '@umanforge/common/assets/styles/styles.css';

/* ===== Required modules ===== */
import { useEffect, useState } from 'react';
import { Loading } from './components/loading.component';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AxiosHeaders, AxiosResponse } from 'axios';
import { API, AuthSignIn, AXIOS, clearToken, clearUser, CommonGlobalService, LanguageService, RootState, saveUser, UserUmanforge, saveToken, LoggerService } from '@umanforge/common';
import { useDispatch, useSelector } from 'react-redux';

/* ===== Required constants ===== */
import { ROUTES } from './constants/navigation.constants';

/* ===== Required components ===== */
import { Copyright } from './components/menu/copyright.component';
import { LanguageSelector } from './components/menu/language_selector.component';

/* ===== Required views ===== */
import { HomeView } from './views/home.view';
import { UsersView } from './views/users.view';
import { ClientsView } from './views/clients.view';
import { SitesView } from './views/sites.view';
import { SectorsView } from './views/sectors.view';
import { AbilitiesView } from './views/abilities.view';
import { SkillsView } from './views/skills.view';
import { CreateUserView } from './views/create_user.view';
import { DetailsUserView } from './views/details_user.view';
import { CreateClientView } from './views/create_client.view';
import { DetailsClientsView } from './views/details_client.view';
import { CreateSiteView } from './views/create_site.view';
import { DetailsSiteView } from './views/details_site.view';
import { CreateSectorView } from './views/create_sector.view';
import { DetailsSectorsView } from './views/details_sector.view';
import { CreateAbilityView } from './views/create_ability.view';
import { DetailsAbilityView } from './views/details_ability.view';
import { CreateSkillView } from './views/create_skill.view';
import { DetailsSkillView } from './views/details_skill.view';
import { EquipmentsView } from 'features/equipments/views/equipments.view';
import { DetailsEquipmentView } from 'features/equipments/views/details_equipment.view';
import { CreateEquipmentView } from 'features/equipments/views/create_equipment.view';
import { DriveView } from './views/drive.view';
import { VERSION } from 'constants/app.constants';


const logger: LoggerService = new LoggerService( 'App' );

export function App() {

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();
  const [ isCheckingAuth, setIsCheckingAuth ] = useState<boolean>( true );

  useEffect( () => {
    logger.verbose( 'Version :', VERSION );
  }, [] );

  useEffect( () => {
    const urlParams = new URLSearchParams( window.location.search );
    const umanforge = urlParams.get( 'umanforge' );
    if ( umanforge != null && umanforge != '' ) {
      window.history.pushState( {}, document.title, window.location.pathname );
    }
    if ( !token ) {
      if ( window.location.pathname !== ROUTES.FIRST_USER_CLIENT ) {
        AXIOS.get( API.IS_SIGN_IN )
          .then( ( response: AxiosResponse ) => {
            let connected = false;
            let user: UserUmanforge | null = null;
            let newToken: string | null = null;
            if ( response.status === 200 ) {
              const result = response.data as AuthSignIn;
              newToken = ( response.headers as AxiosHeaders ).get( 'auth-token' ) as string;
              user = result.user as UserUmanforge;
              connected = result.signin;
            }
            if ( !connected || user == null || user.needChangePassword ) {
              dispatch( clearToken() );
              dispatch( clearUser() );
              CommonGlobalService.openAuthPage();
            } else {
              dispatch( saveToken( newToken as string ) );
              dispatch( saveUser( user as UserUmanforge ) );
              setIsCheckingAuth( false );
            }
          } )
          .catch( () => {
            CommonGlobalService.openAuthPage();
          } );
      } else {
        setIsCheckingAuth( false );
      }
    } else {
      setIsCheckingAuth( false );
    }
  }, [ token, dispatch ] );

  useEffect( () => {
    const checkUserConnection = () => {
      if ( !document.hidden ) {
        AXIOS.get( API.IS_SIGN_IN )
          .then( ( response: AxiosResponse ) => {
            let connected = false;
            let user: UserUmanforge | null = null;
            let newToken: string | null = null;
            if ( response.status === 200 ) {
              const result = response.data as AuthSignIn;
              newToken = ( response.headers as AxiosHeaders ).get( 'auth-token' ) as string;
              user = result.user as UserUmanforge;
              connected = result.signin;
            }
            if ( !connected || user == null || user.needChangePassword ) {
              dispatch( clearToken() );
              dispatch( clearUser() );
              CommonGlobalService.openAuthPage();
            } else {
              dispatch( saveToken( newToken as string ) );
              dispatch( saveUser( user as UserUmanforge ) );
            }
          } )
          .catch( () => {
            CommonGlobalService.openAuthPage();
          } );
      }
    };

    document.addEventListener( 'visibilitychange', checkUserConnection );

    return () => {
      document.removeEventListener( 'visibilitychange', checkUserConnection );
    };
  }, [ dispatch ] );

  if ( isCheckingAuth ) {
    return (
      <div className='flex w-screen h-screen items-center justify-center'>
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div className='h-[100%] mb-16 ml-16'>
        <header>
          <Router>
            <Routes>
              {/* ...existing code... */}
              <Route path={ ROUTES.HOME } element={<HomeView />} />

              <Route path={ ROUTES.USERS } element={<UsersView />} />
              <Route path={ ROUTES.USER } element={<DetailsUserView />} />
              <Route path={ ROUTES.NEW_USER } element={<CreateUserView />} />

              <Route path={ ROUTES.CLIENTS } element={<ClientsView />} />
              <Route path={ ROUTES.CLIENT } element={<DetailsClientsView />} />
              <Route path={ ROUTES.NEW_CLIENT } element={<CreateClientView />} />

              <Route path={ ROUTES.SITES } element={<SitesView />} />
              <Route path={ ROUTES.SITE } element={<DetailsSiteView />} />
              <Route path={ ROUTES.NEW_SITE } element={<CreateSiteView />} />

              <Route path={ ROUTES.SECTORS } element={<SectorsView />} />
              <Route path={ ROUTES.SECTOR } element={<DetailsSectorsView />} />
              <Route path={ ROUTES.NEW_SECTOR } element={<CreateSectorView />} />

              <Route path={ ROUTES.ABILITIES } element={<AbilitiesView />} />
              <Route path={ ROUTES.ABILITY } element={<DetailsAbilityView />} />
              <Route path={ ROUTES.NEW_ABILITY } element={<CreateAbilityView />} />

              <Route path={ ROUTES.SKILLS } element={<SkillsView />} />
              <Route path={ ROUTES.SKILL } element={<DetailsSkillView />} />
              <Route path={ ROUTES.NEW_SKILL } element={<CreateSkillView />} />

              <Route path={ ROUTES.EQUIPMENTS } element={<EquipmentsView />} />
              <Route path={ ROUTES.EQUIPMENT } element={<DetailsEquipmentView />} />
              <Route path={ ROUTES.NEW_EQUIPMENT  } element={<CreateEquipmentView />} />

              <Route path={ ROUTES.DRIVE } element={<DriveView />} />
            </Routes>
          </Router>
        </header>
      </div>

      <footer className='fixed h-16 gap-4 bottom-0 w-full bg-white border-t border-gray-300 flex items-center justify-between py-2 px-4 z-20'>
        <div className='text-center ml-20 text-nowrap truncate min-w-[65px]'>
          <Copyright />
        </div>
        <div className='flex justify-end min-w-[85px]'>
          <LanguageSelector
            menuPlacement='top'
            onChange={ newLang => {
              LanguageService.changeLanguage( newLang.value );
            } }
          />
        </div>
      </footer>
    </div>
  );
}